/**
 * Analytics Types and Event Definitions for PostHog
 * Strategic event tracking following PostHog's [object]_[verb] naming convention
 */

// Core Events for LearnYourself App
export const ANALYTICS_EVENTS = {
  // User Lifecycle Events
  USER_SIGNED_UP: 'user_signed_up',
  USER_VERIFIED_EMAIL: 'user_verified_email',
  USER_ONBOARDED: 'user_onboarded',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  
  // Subscription Events
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  PRICING_PAGE_VIEWED: 'pricing_page_viewed',
  UPGRADE_BUTTON_CLICKED: 'upgrade_button_clicked',
  
  // Document Events (Core Feature)
  DOCUMENT_UPLOADED: 'document_uploaded',
  DOCUMENT_ANALYZED: 'document_analyzed',
  DOCUMENT_DELETED: 'document_deleted',
  DOCUMENT_REPROCESSED: 'document_reprocessed',
  UPLOAD_ZONE_ACCESSED: 'upload_zone_accessed',
  
  // Chat Events (Core Feature)
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_CONVERSATION_STARTED: 'chat_conversation_started',
  CHAT_DOCUMENT_REFERENCED: 'chat_document_referenced',
  SLASH_COMMAND_USED: 'slash_command_used',
  
  // Engagement Events
  SESSION_EXTENDED: 'session_extended', // > 10 minutes
  FEATURE_DISCOVERED: 'feature_discovered',
  HELP_ACCESSED: 'help_accessed',
  TUTORIAL_COMPLETED: 'tutorial_completed',
  
  // Page Events
  PAGE_EXIT_DETAILED: 'page_exit_detailed',
  SESSION_HEARTBEAT: 'session_heartbeat',
  
  // Button Tracking Events
  BUTTON_CLICKED: 'button_clicked',
  CTA_CLICKED: 'cta_clicked',
  NAVIGATION_CLICKED: 'navigation_clicked',
  
  // Error Events
  ERROR_ENCOUNTERED: 'error_encountered',
  UPLOAD_FAILED: 'upload_failed',
  CONNECTION_LOST: 'connection_lost'
} as const;

// Type-safe event names
export type AnalyticsEventName = keyof typeof ANALYTICS_EVENTS;

// Common Properties Interface
export interface BaseAnalyticsProperties {
  page_path?: string;
  timestamp?: string;
  user_agent_mobile?: boolean;
  session_id?: string;
}

// Specific Event Properties
export interface UserSignupProperties extends BaseAnalyticsProperties {
  signup_method: 'email' | 'google' | 'github';
  referrer_source?: string;
  utm_campaign?: string;
  utm_source?: string;
}

export interface DocumentUploadProperties extends BaseAnalyticsProperties {
  document_type: string;
  file_size_mb: number;
  file_extension: string;
  upload_method: 'drag_drop' | 'click_upload' | 'paste';
  processing_time_ms?: number;
}

export interface ChatMessageProperties extends BaseAnalyticsProperties {
  message_length: number;
  has_document_context: boolean;
  conversation_id: string;
  message_type: 'question' | 'follow_up' | 'clarification';
  documents_referenced?: number;
}

export interface ButtonClickProperties extends BaseAnalyticsProperties {
  button_name: string;
  button_type: 'primary' | 'secondary' | 'tertiary' | 'link';
  context: string;
  element_position?: string;
  page_section?: string;
}

export interface SubscriptionProperties extends BaseAnalyticsProperties {
  plan_type: 'free' | 'pro' | 'enterprise';
  billing_cycle: 'monthly' | 'yearly';
  price_cents: number;
  discount_applied?: boolean;
  payment_method?: string;
}

export interface PageExitProperties extends BaseAnalyticsProperties {
  time_on_page_seconds: number;
  scroll_depth_percentage: number;
  exit_method: 'visibility_change' | 'beforeunload' | 'pagehide';
  interactions_count?: number;
}

export interface ErrorProperties extends BaseAnalyticsProperties {
  error_message: string;
  error_type: string;
  component_name?: string;
  stack_trace?: string;
}

// Button Priority Levels for Strategic Tracking
export enum ButtonPriority {
  HIGH = 'high',      // Always track (conversion-critical)
  MEDIUM = 'medium',  // Selectively track
  LOW = 'low',        // Avoid tracking
  EXCLUDED = 'excluded' // Never track
}

// Button Classification
export interface ButtonConfig {
  name: string;
  priority: ButtonPriority;
  category: 'conversion' | 'navigation' | 'feature' | 'ui' | 'social';
  track_automatically?: boolean;
}

// Pre-defined button configurations
export const BUTTON_CONFIGS: Record<string, ButtonConfig> = {
  // HIGH PRIORITY - Always Track
  'signup_button': { name: 'Sign Up', priority: ButtonPriority.HIGH, category: 'conversion', track_automatically: true },
  'login_button': { name: 'Log In', priority: ButtonPriority.HIGH, category: 'conversion', track_automatically: true },
  'upgrade_button': { name: 'Upgrade', priority: ButtonPriority.HIGH, category: 'conversion', track_automatically: true },
  'subscribe_button': { name: 'Subscribe', priority: ButtonPriority.HIGH, category: 'conversion', track_automatically: true },
  'upload_document': { name: 'Upload Document', priority: ButtonPriority.HIGH, category: 'feature', track_automatically: true },
  'start_chat': { name: 'Start Chat', priority: ButtonPriority.HIGH, category: 'feature', track_automatically: true },
  'send_message': { name: 'Send Message', priority: ButtonPriority.HIGH, category: 'feature', track_automatically: true },
  
  // MEDIUM PRIORITY - Selectively Track  
  'pricing_view': { name: 'View Pricing', priority: ButtonPriority.MEDIUM, category: 'navigation' },
  'features_explore': { name: 'Explore Features', priority: ButtonPriority.MEDIUM, category: 'navigation' },
  'document_delete': { name: 'Delete Document', priority: ButtonPriority.MEDIUM, category: 'feature' },
  'profile_edit': { name: 'Edit Profile', priority: ButtonPriority.MEDIUM, category: 'feature' },
  
  // LOW PRIORITY - Avoid Tracking
  'menu_toggle': { name: 'Menu Toggle', priority: ButtonPriority.LOW, category: 'ui' },
  'close_modal': { name: 'Close Modal', priority: ButtonPriority.LOW, category: 'ui' },
  'pagination_next': { name: 'Next Page', priority: ButtonPriority.LOW, category: 'navigation' },
  
  // EXCLUDED - Never Track
  'theme_toggle': { name: 'Theme Toggle', priority: ButtonPriority.EXCLUDED, category: 'ui' },
  'tooltip_close': { name: 'Close Tooltip', priority: ButtonPriority.EXCLUDED, category: 'ui' }
};

// Analytics Configuration
export interface AnalyticsConfig {
  enabled: boolean;
  debug: boolean;
  batch_size: number;
  batch_timeout_ms: number;
  track_internal_users: boolean;
  sample_rate: number; // 0-1, for high-traffic scenarios
}

// Event Batching Interface
export interface BatchedEvent {
  name: string;
  properties: Record<string, any>;
  timestamp: number;
}

// Hook Return Types
export interface UseAnalyticsReturn {
  trackEvent: (eventName: string, properties?: Record<string, any>) => void;
  trackButtonClick: (buttonName: string, context?: string, additionalProps?: Record<string, any>) => void;
  trackError: (error: Error, context?: string) => void;
  trackPageView: (pageName?: string, additionalProps?: Record<string, any>) => void;
  identify: (userId: string, userProperties?: Record<string, any>) => void;
  reset: () => void;
  isLoaded: boolean;
}

// Session Tracking Types
export interface SessionData {
  session_id: string;
  start_time: number;
  page_views: number;
  interactions: number;
  last_activity: number;
}

export interface PageViewData {
  page_path: string;
  page_name: string;
  entry_time: number;
  exit_time?: number;
  scroll_depth: number;
  interactions: number;
}